import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Empty, Stat, fieldCls } from "@/components/dashboard/Shared";

const CURRENCIES = ["USD", "ARS", "MXN", "COP", "CLP", "PEN", "UYU", "BRL"];
const PACKS = [[5, 10], [15, 27], [40, 64]];
const money = (n, c) => `${c} ${Number(n).toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;

export function PaySettings() {
  const api = useApi();
  const [v, setV] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api("get", "/admin/settings/payments").then(setV).catch(() => setFailed(true)); }, [api]);
  if (failed) return <Empty>No pudimos cargar los ajustes de pago. Recarga la página.</Empty>;
  if (!v) return null;
  const save = async () => {
    try { setV(await api("put", "/admin/settings/payments", { mp_currency: v.mp_currency, usd_rate: Number(v.usd_rate) })); toast.success("Ajustes guardados"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="pay-settings" className="max-w-2xl space-y-5 rounded-xl border border-white/10 bg-[#12121A] p-6">
      <div>
        <p className="font-heading text-lg font-bold text-white">Precios con Mercado Pago</p>
        <p className="text-sm text-slate-400">Los paquetes se cobran en esta moneda con la cotización que fijes. Stripe sigue cobrando en dólares.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Select value={v.mp_currency} onValueChange={(c) => setV({ ...v, mp_currency: c, usd_rate: c === "USD" ? 1 : v.usd_rate })}>
          <SelectTrigger data-testid="pay-settings-currency" className={fieldCls}><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/10 bg-[#181824] text-slate-200">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-slate-400">1 USD =
          <Input data-testid="pay-settings-rate" type="number" min="0" step="any" disabled={v.mp_currency === "USD"} value={v.usd_rate} onChange={(e) => setV({ ...v, usd_rate: e.target.value })} className={fieldCls} />
          {v.mp_currency}
        </label>
      </div>
      <div className="space-y-1 font-mono text-xs text-slate-400">
        {PACKS.map(([c, usd]) => <p key={c}>{c} créditos: USD {usd} → <span className="text-white">{money(usd * Number(v.usd_rate || 0), v.mp_currency)}</span></p>)}
      </div>
      <Button data-testid="pay-settings-save" onClick={save} className="rounded-full bg-[#FF4500] text-white hover:bg-[#ff5a1f]">Guardar</Button>
    </div>
  );
}

const PAYOUT_TONE = { requested: "bg-amber-500/15 text-amber-400", paid: "bg-emerald-500/15 text-emerald-400", rejected: "bg-red-500/15 text-red-400" };
const PAYOUT_TEXT = { requested: "Pendiente", paid: "Pagado", rejected: "Rechazado" };
const PayoutBadge = ({ p }) => <span data-testid={`payout-status-${p.id}`} className={`inline-flex rounded-full px-2.5 py-1 font-mono text-[10px] tracking-wider ${PAYOUT_TONE[p.status]}`}>{PAYOUT_TEXT[p.status].toUpperCase()}</span>;

function PayoutRow({ p, admin, reload }) {
  const api = useApi();
  const [note, setNote] = useState("");
  const decide = async (paid) => {
    try { await api("post", `/admin/payouts/${p.id}`, { paid, note }); toast.success(paid ? "Marcado como pagado" : "Retiro rechazado: saldo devuelto"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`payout-${p.id}`} className="space-y-3 border-b border-white/5 p-4 last:border-0">
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-white">USD {p.amount_usd.toFixed(2)} {admin && <span className="font-normal text-slate-400">· {p.name || p.email}</span>}</p>
          <p className="text-xs text-slate-500">{fmtDate(p.created_at)} · {p.method}: {p.details}</p>
          {p.note && <p className="mt-1 text-xs text-slate-400">Nota: {p.note}</p>}
        </div>
        <PayoutBadge p={p} />
      </div>
      {admin && p.status === "requested" && (
        <div className="flex flex-wrap gap-2">
          <Input data-testid={`payout-note-${p.id}`} placeholder="Nota (ej: n° de transferencia)" value={note} onChange={(e) => setNote(e.target.value)} className={`h-9 max-w-sm ${fieldCls}`} />
          <Button size="sm" data-testid={`payout-paid-${p.id}`} onClick={() => decide(true)} className="rounded-full bg-emerald-500 text-white hover:bg-emerald-600">Marcar pagado</Button>
          <Button size="sm" variant="outline" data-testid={`payout-reject-${p.id}`} onClick={() => decide(false)} className="rounded-full border-red-400/40 bg-transparent text-red-300 hover:bg-red-500/10">Rechazar</Button>
        </div>
      )}
    </div>
  );
}

export function PayoutsAdmin() {
  const api = useApi();
  const [list, setList] = useState(null);
  const reload = () => api("get", "/admin/payouts").then(setList).catch(() => setList([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);
  if (!list) return null;
  if (!list.length) return <Empty>No hay pedidos de retiro.</Empty>;
  const pending = list.filter((p) => p.status === "requested").reduce((a, p) => a + p.amount_usd, 0);
  return (
    <div className="space-y-4">
      <p data-testid="payouts-pending-total" className="font-mono text-xs text-slate-400">PENDIENTE DE PAGO: USD {pending.toFixed(2)}</p>
      <div data-testid="payouts-admin" className="rounded-xl border border-white/10 bg-[#12121A]">{list.map((p) => <PayoutRow key={p.id} p={p} admin reload={reload} />)}</div>
    </div>
  );
}

export function CuratorPayouts() {
  const api = useApi();
  const { me, refresh } = useMe();
  const [list, setList] = useState([]);
  const [method, setMethod] = useState("Transferencia (CBU/CVU/Alias)");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const reload = () => api("get", "/curator/payouts").then(setList).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);
  const balance = Number(me?.earnings_usd || 0);
  const pending = list.some((p) => p.status === "requested");
  const submit = async () => {
    setBusy(true);
    try { await api("post", "/curator/payouts", { method, details }); toast.success("Pedido de retiro enviado"); setDetails(""); reload(); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <Stat id="earnings" label="Saldo disponible (70%)" value={`USD ${balance.toFixed(2)}`} accent />
        <Stat id="paid-total" label="Ya cobrado" value={`USD ${list.filter((p) => p.status === "paid").reduce((a, p) => a + p.amount_usd, 0).toFixed(2)}`} />
      </div>
      <div data-testid="payout-form" className="max-w-2xl space-y-3 rounded-xl border border-white/10 bg-[#12121A] p-6">
        <p className="font-heading text-lg font-bold text-white">Pedir retiro</p>
        <p className="text-sm text-slate-400">Retiras todo tu saldo disponible (mínimo USD 10). Te pagamos dentro de los 7 días hábiles.</p>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger data-testid="payout-method" className={fieldCls}><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/10 bg-[#181824] text-slate-200">
            {["Transferencia (CBU/CVU/Alias)", "Mercado Pago", "PayPal", "Otro"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Textarea data-testid="payout-details" placeholder="Tus datos de cobro (alias, CBU/CVU, email de PayPal, titular...)" value={details} onChange={(e) => setDetails(e.target.value)} className={fieldCls} />
        <Button data-testid="payout-submit" disabled={busy || pending || balance < 10 || details.trim().length < 3} onClick={submit} className="rounded-full bg-[#FF4500] text-white hover:bg-[#ff5a1f]">
          {pending ? "Tienes un retiro pendiente" : balance < 10 ? "Necesitas al menos USD 10" : `Retirar USD ${balance.toFixed(2)}`}
        </Button>
      </div>
      {list.length > 0 && <div data-testid="payout-history" className="rounded-xl border border-white/10 bg-[#12121A]">{list.map((p) => <PayoutRow key={p.id} p={p} reload={reload} />)}</div>}
    </div>
  );
}
